/**
 * AlgorithmConfidenceScore — Day 7 dual-engine transparency banner.
 *
 * The product positioning: "We disagree with our own AI on X% of opportunities.
 * Both views are shown — you decide." Honest dual-engine framing.
 *
 * Computes agreement rate from cached opportunity_analyses on the BACKEND;
 * this component just renders the supplied numbers. Lives ABOVE the
 * side-by-side view so users see the honesty upfront.
 */
import React from "react";
import { Scale } from "lucide-react";

const AlgorithmConfidenceScore = ({ agreementRate, totalOpps, agreedCount }) => {
  if (agreementRate === null || agreementRate === undefined) return null;

  const pct = Math.round(agreementRate * 100);
  const disagreedCount = totalOpps - agreedCount;

  // Tone: high agreement = green (boring), low = amber (interesting)
  const tone =
    pct >= 80
      ? { bar: "bg-emerald-500", chip: "bg-emerald-50 border-emerald-300 text-emerald-800" }
      : pct >= 40
      ? { bar: "bg-amber-500", chip: "bg-amber-50 border-amber-300 text-amber-800" }
      : { bar: "bg-[#7C3AED]", chip: "bg-[#7C3AED]/10 border-[#7C3AED]/30 text-[#7C3AED]" };

  return (
    <div
      data-testid="algorithm-confidence-score"
      className="border border-zinc-200 bg-white p-3 space-y-2"
    >
      <div className="flex items-start gap-2.5">
        <div className={`w-9 h-9 ${tone.bar} flex items-center justify-center shrink-0`}>
          <Scale size={18} className="text-white" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h4 className="text-sm font-black tracking-tight">Algorithm Confidence</h4>
            <span
              className={`text-[10px] font-mono px-1.5 py-0.5 border ${tone.chip}`}
              data-testid="confidence-score-pct"
            >
              {pct}% agreement
            </span>
          </div>
          <p className="text-xs text-zinc-600 leading-snug mt-1">
            Quantitative algorithm + <strong>Opus 4.8</strong> reasoning:{" "}
            <strong>{pct}% agreement</strong> on current opportunities (n={totalOpps}).{" "}
            {disagreedCount > 0
              ? "Disagreements are surfaced explicitly so you decide."
              : "When they disagree, we surface it explicitly — conviction is high when both align."}
          </p>
        </div>
      </div>

      {disagreedCount > 0 && (
        <div className="pt-2 border-t border-zinc-100 flex items-center gap-2 text-[11px] text-zinc-500">
          <div className="h-1.5 bg-zinc-100 flex-1 overflow-hidden">
            <div className={`h-full ${tone.bar}`} style={{ width: `${pct}%` }} />
          </div>
          <span className="font-mono shrink-0">
            {agreedCount} ✓ &middot; {disagreedCount} ⚠
          </span>
        </div>
      )}

      <p className="text-[10px] text-zinc-400 leading-tight">
        Designed for disagreement-surfacing — when the algorithm and Opus 4.8
        disagree, both views are shown explicitly. Current high agreement reflects
        a small sample &amp; signal set; expected to widen with V1 richer signals.
      </p>
    </div>
  );
};

export default AlgorithmConfidenceScore;
